import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Stack,
  TableSortLabel,
} from "@mui/material";

import BugReportIcon from "@mui/icons-material/BugReport";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GroupWorkIcon from "@mui/icons-material/GroupWork";

import { COLORS, SCROLLBAR_STYLES, findKey } from "./utils/rtmUtils";
import AnalyticsKPICard from "./components/AnalyticsKPICard";
import AnalyticsFilterBar from "./components/AnalyticsFilterBar";
import AnalyticsChart from "./components/AnalyticsChart";

const TestRTMDashboard = ({ data }) => {
  const [filters, setFilters] = useState({
    monthYear: ["All"],
    version: ["All"],
    priority: ["All"],
    status: ["All"],
    type: ["All"],

    labels: ["All"],
  });

  const [sortConfig, setSortConfig] = useState({
    key: "key",
    direction: "asc",
  });
  const [tableFilter, setTableFilter] = useState("All");

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const sample = data[0];
    const actualKeys = {
      key: findKey(sample, "Issue Key") || "Issue key",
      type: findKey(sample, "Issue Type") || "Issue Type",
      summary: findKey(sample, "Summary") || "Summary",
      linkedId: findKey(sample, "Linked Issues ID") || "Linked Issue Key",
      linkedType: findKey(sample, "Linked Issue Type") || "Linked Issue Type",
      status: findKey(sample, "Status") || "Status",
      priority: findKey(sample, "Priority") || "Priority",
      created:
        findKey(sample, "Created Date") ||
        findKey(sample, "Created") ||
        "Created",
      project: findKey(sample, "Project") || "Project",
      creator: findKey(sample, "Creator") || "Creator",
      labels: findKey(sample, "Labels") || "Labels",
      sprint: findKey(sample, "Sprint") || "Sprint",
      version:
        findKey(sample, "Release Version") ||
        findKey(sample, "Version") ||
        "Fix Version/s",
    };

    const aggregated = {};

    data.forEach((row) => {
      const issueKey = row[actualKeys.key];
      if (!issueKey) return;

      if (!aggregated[issueKey]) {
        aggregated[issueKey] = {
          key: issueKey,
          type: row[actualKeys.type] || "Unknown",
          summary: row[actualKeys.summary] || "",
          status: row[actualKeys.status] || "To Do",
          priority: row[actualKeys.priority] || "Medium",
          created: row[actualKeys.created],
          project: row[actualKeys.project] || "N/A",
          creator: row[actualKeys.creator] || "Unknown",
          labels: row[actualKeys.labels] || "",
          sprint: row[actualKeys.sprint] || "No Sprint",
          version: row[actualKeys.version] || "No Version",
          linkedStories: new Set(),
          linkedBugs: new Set(),
          linkedTasks: new Set(),
          linkedOther: new Set(),
          allLinks: new Set(),
        };
      }

      const lIdRaw = row[actualKeys.linkedId];
      const lTypeRaw = row[actualKeys.linkedType];

      if (lIdRaw) {
        const ids = String(lIdRaw)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const types = lTypeRaw
          ? String(lTypeRaw)
              .split(",")
              .map((s) => s.trim())
          : [];

        ids.forEach((id, index) => {
          const type = (types[index] || types[0] || "").toLowerCase();
          aggregated[issueKey].allLinks.add(id);

          if (
            type.includes("story") ||
            type.includes("requirement") ||
            id.toUpperCase().startsWith("STORY")
          ) {
            aggregated[issueKey].linkedStories.add(id);
          } else if (
            type.includes("bug") ||
            type.includes("defect") ||
            id.toUpperCase().startsWith("BUG")
          ) {
            aggregated[issueKey].linkedBugs.add(id);
          } else if (type.includes("task")) {
            aggregated[issueKey].linkedTasks.add(id);
          } else {
            aggregated[issueKey].linkedOther.add(id);
          }
        });
      }
    });

    return Object.values(aggregated).map((test) => {
      const storyCount = test.linkedStories.size;
      const bugCount = test.linkedBugs.size;
      const taskCount = test.linkedTasks.size;
      const otherCount = test.linkedOther.size;
      const totalLinks = test.allLinks.size;

      return {
        ...test,
        storyCount,
        bugCount,
        taskCount,
        otherCount,
        totalLinks,
        isOrphan: totalLinks === 0,
        isDefectLinked: bugCount > 0,
        isCovered: storyCount > 0,
        createdDate: test.created ? new Date(test.created) : null,
        monthYear: test.created
          ? new Date(test.created).toLocaleString("default", {
              month: "short",
              year: "numeric",
            })
          : "Unknown",
      };
    });
  }, [data]);

  const filterOptions = useMemo(() => {
    const getOptions = (key) =>
      ["All", ...new Set(processedData.map((d) => String(d[key])))].filter(
        (v) => v !== "undefined" && v !== "null",
      );
    const allLabels = new Set(["All"]);
    processedData.forEach((d) => {
      if (d.labels)
        String(d.labels)
          .split(",")
          .forEach((l) => allLabels.add(l.trim()));
    });

    return {
      monthYear: getOptions("monthYear").filter((m) => m !== "Unknown"),
      version: getOptions("version"),
      priority: getOptions("priority"),
      status: getOptions("status"),
      type: getOptions("type"),
      creator: getOptions("creator"),
      labels: Array.from(allLabels),
    };
  }, [processedData]);

  const filteredData = useMemo(
    () =>
      processedData.filter((d) => {
        const labelMatch =
          filters.labels.includes("All") ||
          (d.labels &&
            String(d.labels)
              .split(",")
              .some((l) => filters.labels.includes(l.trim())));
        return (
          (filters.monthYear.includes("All") ||
            filters.monthYear.includes(String(d.monthYear))) &&
          (filters.version.includes("All") ||
            filters.version.includes(String(d.version))) &&
          (filters.priority.includes("All") ||
            filters.priority.includes(String(d.priority))) &&
          (filters.status.includes("All") ||
            filters.status.includes(String(d.status))) &&
          (filters.type.includes("All") ||
            filters.type.includes(String(d.type))) &&
          labelMatch
        );
      }),
    [processedData, filters],
  );

  const stats = useMemo(() => {
    const total = filteredData.length;
    const linkedToStories = filteredData.filter((d) => d.storyCount > 0).length;
    const orphanTests = filteredData.filter((d) => d.isOrphan).length;
    const linkedToBugs = filteredData.filter((d) => d.bugCount > 0).length;
    const totalLinks = filteredData.reduce((acc, d) => acc + d.totalLinks, 0);
    const avgLinks = total > 0 ? (totalLinks / total).toFixed(1) : 0;

    return {
      total,
      linkedToStories,
      orphanTests,
      linkedToBugs,
      avgLinks,
      coveragePercent:
        total > 0 ? Math.round((linkedToStories / total) * 100) : 0,
    };
  }, [filteredData]);

  const insights = useMemo(() => {
    const list = [];
    const highPrioOrphan = filteredData.filter((d) => {
      const p = String(d.priority).toUpperCase();
      return (
        (p.includes("HIGH") ||
          p.includes("CRITICAL") ||
          p.includes("SEVERE")) &&
        d.isOrphan
      );
    });
    if (highPrioOrphan.length > 0)
      list.push({
        text: `${highPrioOrphan.length} High/Critical priority tests are orphans`,
        type: "error",
      });
    if (stats.orphanTests > 0)
      list.push({
        text: `${stats.orphanTests} tests are orphan (no links)`,
        type: "error",
      });

    const storyMap = {};
    filteredData.forEach((d) =>
      d.linkedStories.forEach((s) => (storyMap[s] = (storyMap[s] || 0) + 1)),
    );
    const underTested = Object.entries(storyMap).filter(
      ([id, count]) => count === 1,
    );
    if (underTested.length > 0)
      list.push({
        text: `Story ${underTested[0][0]} has only 1 test mapping`,
        type: "warning",
      });

    const highLinks = [...filteredData]
      .filter((d) => d.totalLinks > 5)
      .sort((a, b) => b.totalLinks - a.totalLinks);
    if (highLinks.length > 0)
      list.push({
        text: `Test ${highLinks[0].key} has excessive mappings (${highLinks[0].totalLinks})`,
        type: "warning",
      });
    if (filteredData.filter((d) => d.isDefectLinked).length > 0)
      list.push({
        text: `${filteredData.filter((d) => d.isDefectLinked).length} tests linked to bugs`,
        type: "success",
      });

    return list;
  }, [filteredData, stats]);

  const tableData = useMemo(() => {
    let data = [...filteredData];
    if (tableFilter === "Covered") data = data.filter((d) => !d.isOrphan);
    else if (tableFilter === "Not Covered")
      data = data.filter((d) => d.isOrphan);

    data.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (sortConfig.key === "createdDate") {
        aVal = aVal ? aVal.getTime() : 0;
        bVal = bVal ? bVal.getTime() : 0;
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredData, tableFilter, sortConfig]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // --- Chart Data Memos ---

  const creationTrendData = useMemo(() => {
    const months = [...new Set(filteredData.map((d) => d.monthYear))]
      .filter((m) => m !== "Unknown")
      .sort((a, b) => new Date(a) - new Date(b));
    return {
      labels: months,
      datasets: [
        {
          label: "Tests Created",
          data: months.map(
            (m) => filteredData.filter((d) => d.monthYear === m).length,
          ),
          borderColor: COLORS.PRIMARY,
          backgroundColor: `${COLORS.PRIMARY}15`,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [filteredData]);

  const coverageDistData = useMemo(() => {
    const versions = [...new Set(filteredData.map((d) => d.version))].sort();
    return {
      labels: versions,
      datasets: [
        {
          label: "Linked Tests",
          data: versions.map(
            (v) =>
              filteredData.filter((d) => d.version === v && !d.isOrphan).length,
          ),
          backgroundColor: COLORS.PRIMARY,
        },
        {
          label: "Orphan Tests",
          data: versions.map(
            (v) =>
              filteredData.filter((d) => d.version === v && d.isOrphan).length,
          ),
          backgroundColor: COLORS.FAIL,
        },
      ],
    };
  }, [filteredData]);

  const storyCoverageData = useMemo(() => {
    const storyMap = {};
    filteredData.forEach((d) =>
      d.linkedStories.forEach((s) => (storyMap[s] = (storyMap[s] || 0) + 1)),
    );
    const entries = Object.entries(storyMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15);
    return {
      labels: entries.map((e) => e[0]),
      datasets: [
        {
          label: "Number of Tests",
          data: entries.map((e) => e[1]),
          backgroundColor: COLORS.DONE,
        },
      ],
    };
  }, [filteredData]);

  const bugCoverageData = useMemo(() => {
    const bugMap = {};
    filteredData.forEach((d) =>
      d.linkedBugs.forEach((b) => (bugMap[b] = (bugMap[b] || 0) + 1)),
    );
    const entries = Object.entries(bugMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15);
    return {
      labels: entries.map((e) => e[0]),
      datasets: [
        {
          label: "Linked Tests",
          data: entries.map((e) => e[1]),
          backgroundColor: COLORS.FAIL,
        },
      ],
    };
  }, [filteredData]);

  const linkDistData = useMemo(
    () => ({
      labels: ["Stories", "Bugs", "Tasks", "Other"],
      datasets: [
        {
          label: "Link Counts",
          data: [
            filteredData.reduce((acc, d) => acc + d.storyCount, 0),
            filteredData.reduce((acc, d) => acc + d.bugCount, 0),
            filteredData.reduce((acc, d) => acc + d.taskCount, 0),
            filteredData.reduce((acc, d) => acc + d.otherCount, 0),
          ],
          backgroundColor: COLORS.PALETTE,
        },
      ],
    }),
    [filteredData],
  );

  const statusDistData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => {
      acc[d.status] = (acc[d.status] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: Object.keys(counts),
      datasets: [
        { data: Object.values(counts), backgroundColor: COLORS.PALETTE },
      ],
    };
  }, [filteredData]);

  const priorityDistData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => {
      acc[d.priority] = (acc[d.priority] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: Object.keys(counts),
      datasets: [
        {
          label: "Tests",
          data: Object.values(counts),
          backgroundColor: COLORS.TO_DO,
        },
      ],
    };
  }, [filteredData]);

  const labelDistData = useMemo(() => {
    const labelMap = {};
    filteredData.forEach((d) => {
      if (d.labels)
        String(d.labels)
          .split(",")
          .forEach((l) => {
            const clean = l.trim();
            if (clean) labelMap[clean] = (labelMap[clean] || 0) + 1;
          });
    });
    const entries = Object.entries(labelMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
    return {
      labels: entries.map((e) => e[0]),
      datasets: [
        { data: entries.map((e) => e[1]), backgroundColor: COLORS.PALETTE },
      ],
    };
  }, [filteredData]);

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, md: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          bgcolor: "white",
          boxShadow: "0 10px 30px 0 rgba(0,0,0,0.05)",
        }}
      >
        <AnalyticsFilterBar
          filters={filters}
          options={filterOptions}
          onFilterChange={handleFilterChange}
        />
      </Paper>

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={2.4}>
          <AnalyticsKPICard
            title="Total Tests"
            value={stats.total}
            icon={<AssignmentIcon />}
            color={COLORS.PRIMARY}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <AnalyticsKPICard
            title="Linked Stories"
            value={stats.linkedToStories}
            icon={<CheckCircleOutlineIcon />}
            color={COLORS.DONE}
            subtitle={`${stats.coveragePercent}% Coverage`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <AnalyticsKPICard
            title="Orphan Tests"
            value={stats.orphanTests}
            icon={<WarningAmberIcon />}
            color={COLORS.FAIL}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <AnalyticsKPICard
            title="Linked to Bugs"
            value={stats.linkedToBugs}
            icon={<BugReportIcon />}
            color={COLORS.TO_DO}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <AnalyticsKPICard
            title="Avg Links"
            value={stats.avgLinks}
            icon={<GroupWorkIcon />}
            color={COLORS.OPEN}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Test Creation Distribution
            </Typography>
            <Box sx={{ overflowX: "auto", width: "100%", ...SCROLLBAR_STYLES }}>
              <Box sx={{ height: 350, minWidth: 800 }}>
                <AnalyticsChart
                  type="line"
                  data={creationTrendData}
                  options={{
                    scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
                  }}
                  height={350}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Test Coverage Distribution
            </Typography>
            <Box sx={{ overflowX: "auto", width: "100%", ...SCROLLBAR_STYLES }}>
              <Box sx={{ height: 350, minWidth: 600 }}>
                <AnalyticsChart
                  type="bar"
                  data={coverageDistData}
                  options={{
                    scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
                  }}
                  height={350}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper
            sx={{ p: 3, borderRadius: 3, height: "100%", bgcolor: "#f5f7fa" }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <LightbulbIcon sx={{ color: "#ff9800", mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Smart Insights
              </Typography>
            </Box>
            <Stack spacing={2}>
              {insights.map((insight, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "white",
                    borderLeft: "4px solid",
                    borderLeftColor:
                      insight.type === "error"
                        ? COLORS.FAIL
                        : insight.type === "warning"
                          ? COLORS.TO_DO
                          : COLORS.DONE,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {insight.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Test Coverage per Story
            </Typography>
            <Box sx={{ overflowX: "auto", width: "100%", ...SCROLLBAR_STYLES }}>
              <Box sx={{ height: 340, minWidth: 600 }}>
                <AnalyticsChart
                  type="bar"
                  data={storyCoverageData}
                  height={340}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Labels Distribution
            </Typography>
            <AnalyticsChart
              type="pie"
              data={labelDistData}
              height={350}
              options={{ legend: { position: "right" } }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Bugs Covered by Tests
            </Typography>
            <Box sx={{ overflowX: "auto", width: "100%", ...SCROLLBAR_STYLES }}>
              <Box sx={{ height: 340, minWidth: 600 }}>
                <AnalyticsChart
                  type="bar"
                  data={bugCoverageData}
                  height={340}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Link Type Distribution
            </Typography>
            <Box sx={{ height: 350 }}>
              <AnalyticsChart type="bar" data={linkDistData} height={350} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Priority Distribution
            </Typography>
            <AnalyticsChart type="bar" data={priorityDistData} height={350} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Status Distribution
            </Typography>
            <AnalyticsChart
              type="doughnut"
              data={statusDistData}
              options={{ cutoutPercentage: 70, legend: { position: "right" } }}
              height={350}
            />
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: COLORS.PRIMARY }}
          >
            Test Matrix
          </Typography>
          <Stack direction="row" spacing={1}>
            {["All", "Covered", "Not Covered"].map((f) => (
              <Chip
                key={f}
                label={f}
                onClick={() => setTableFilter(f)}
                color={tableFilter === f ? "primary" : "default"}
                variant={tableFilter === f ? "filled" : "outlined"}
              />
            ))}
          </Stack>
        </Box>
        <TableContainer
          sx={{ maxHeight: 500, overflowY: "auto", ...SCROLLBAR_STYLES }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {[
                  "key",
                  "type",
                  "summary",
                  "status",
                  "priority",
                  "labels",
                  "version",
                  "created",
                  "storyCount",
                  "bugCount",
                ].map((id) => (
                  <TableCell
                    key={id}
                    sx={{
                      bgcolor: "#f8faff",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      ml: 0.5,
                    }}
                  >
                    <TableSortLabel
                      active={sortConfig.key === id}
                      direction={sortConfig.direction}
                      onClick={() =>
                        setSortConfig({
                          key: id,
                          direction:
                            sortConfig.key === id &&
                            sortConfig.direction === "asc"
                              ? "desc"
                              : "asc",
                        })
                      }
                    >
                      {id.toUpperCase()}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: COLORS.PRIMARY,
                      fontSize: "0.65rem",
                    }}
                  >
                    {row.key}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.type}
                      size="small"
                      variant="outlined"
                      sx={{ height: 16, fontSize: "0.6rem" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={row.summary}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.summary}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        bgcolor: ["DONE", "CLOSED", "RESOLVED"].includes(
                          row.status.toUpperCase(),
                        )
                          ? "#e8f5e9"
                          : "#f5f5f5",
                        color: ["DONE", "CLOSED", "RESOLVED"].includes(
                          row.status.toUpperCase(),
                        )
                          ? COLORS.DONE
                          : "#616161",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.65rem" }}>
                    {row.priority}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.65rem" }}>
                    <Tooltip title={row.labels}>
                      <Typography
                        sx={{
                          maxWidth: 100,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.labels}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.65rem" }}>
                    {row.version}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.65rem" }}>
                    {row.monthYear}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {row.storyCount}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {row.bugCount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TestRTMDashboard;
